import type {
  CreateMaintenanceHistoryInput,
  CreateMaintenancePartsInput,
  CreateReminderInput,
  DeleteMaintenanceHistoryInput,
  DeleteMaintenancePartsInput,
  DeleteMaintenanceReminderInput,
  GetLatestMaintenanceInput,
  GetMaintenanceHistoryInput,
  GetMaintenancePartsInput,
  GetReminderListInput,
  GetReminderStatusCountInput,
  ImportMaintenanceBatchInput,
  MaintenanceReminderResponse,
  MaintenanceResponse,
  ReminderStatusCount,
  UpdateMaintenanceHistoryInput,
  UpdateMaintenancePartsInput,
  UpdateReminderInput,
  ValidateMaintenanceImportInput,
  ValidateMaintenanceImportResponse,
} from '@cobliteam/api-lib/src/__generated__/graphql';
import { GraphQLMaintenanceRequester, type MaintenanceReportRequest, type MaintenanceRow, NewMaintenanceRequester } from '@cobliteam/api-lib/src/dashboard/maintenance';
import HttpStatusCode from '@cobliteam/api-lib/src/request/HttpStatusCode';
import type { GraphQLRestDataSourceError } from '@cobliteam/api-lib/src/request/Types';
import { Environment } from '@cobliteam/react-environments';
import Alert from '@cobliteam/ui/components/Alert';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

const env = Environment.getInstance().getEnvironment();

// Lazy instantiation to avoid circular dependency issues
const getNewMaintenanceRequester = () =>
  new NewMaintenanceRequester({
    host: env.apiHost,
  });

const getGraphQLMaintenanceRequester = () => new GraphQLMaintenanceRequester();

const QUERY_KEY = 'MAINTENANCE';

const getNextPageParam = (lastPage: MaintenanceResponse, allPages: MaintenanceResponse[]) => {
  const loadedItems = allPages
    .flat()
    .map((response) => response.rows?.length ?? 0)
    .reduce((acc, a) => acc + a);

  if (lastPage.count === loadedItems) return undefined;
  return allPages.flat().length;
};

const useGetMaintenanceHistory = (request: GetMaintenanceHistoryInput) => {
  return useInfiniteQuery<MaintenanceResponse>({
    initialPageParam: 0,
    queryKey: [QUERY_KEY, 'MAINTENANCE_HISTORY', JSON.stringify(request)],
    queryFn: ({ pageParam = 0 }) =>
      getGraphQLMaintenanceRequester().getMaintenanceHistory({
        ...request,
        page: pageParam as number,
        size: 100,
      }),
    staleTime: 1000 * 60 * 30, // 30 minutes
    getNextPageParam,
  });
};

const useGetMaintenanceParts = (request: GetMaintenancePartsInput) => {
  return useQuery({
    queryKey: [QUERY_KEY, 'MAINTENANCE_PARTS'],
    queryFn: async () => await getGraphQLMaintenanceRequester().getMaintenanceParts(request),
  });
};

const useCreateMaintenanceHistory = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: CreateMaintenanceHistoryInput) => await getGraphQLMaintenanceRequester().createMaintenanceHistory(request),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY, 'MAINTENANCE_HISTORY'],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY, 'MAINTENANCE_REMINDER'],
      });
    },
  });
};

const useUpdateMaintenanceHistory = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: UpdateMaintenanceHistoryInput) => await getGraphQLMaintenanceRequester().updateMaintenanceHistory(request),
    onSuccess: (data, variables) => {
      const maintenanceId = variables.id;
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY, 'MAINTENANCE_HISTORY'],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY, 'MAINTENANCE', maintenanceId],
      });
    },
  });
};

const useDeleteMaintenanceHistory = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: DeleteMaintenanceHistoryInput) => await getGraphQLMaintenanceRequester().deleteMaintenanceHistory(request),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY, 'MAINTENANCE_HISTORY'],
      }),
  });
};

const useCreateMaintenanceParts = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: CreateMaintenancePartsInput) => await getGraphQLMaintenanceRequester().createMaintenanceParts(request),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY, 'MAINTENANCE_PARTS'],
      }),
    onError: (error: GraphQLRestDataSourceError, variables: CreateMaintenancePartsInput) => {
      if (error.status === HttpStatusCode.CONFLICT) {
        Alert.error(`Item ${variables.description} já existe.`);
        return;
      }

      Alert.error('Erro ao criar item. Tente novamente mais tarde.');
    },
  });
};

const useUpdateMaintenanceParts = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: UpdateMaintenancePartsInput) => await getGraphQLMaintenanceRequester().updateMaintenanceParts(request),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY, 'MAINTENANCE_PARTS'],
      }),
  });
};

const useDeleteMaintenanceParts = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: DeleteMaintenancePartsInput) => await getGraphQLMaintenanceRequester().deleteMaintenanceParts(request),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY, 'MAINTENANCE_PARTS'],
      }),
  });
};

const useCreateMaintenanceReminder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: CreateReminderInput) => await getGraphQLMaintenanceRequester().createMaintenanceReminder(request),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY, 'MAINTENANCE_REMINDER'],
      });
      Alert.success('Lembrete criado com sucesso.');
    },
    onError: () => Alert.error('Erro ao criar lembrete. Tente novamente mais tarde.'),
  });
};

const useUpdateMaintenanceReminder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: UpdateReminderInput) => await getGraphQLMaintenanceRequester().updateMaintenanceReminder(request),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY, 'MAINTENANCE_REMINDER'],
      });
      Alert.success('Lembrete editado!', { description: 'Seu lembrete foi editado com sucesso.' });
    },
    onError: () => Alert.error('Não foi possível editar o lembrete. Tente novamente mais tarde.'),
  });
};

const useGetMaintenance = (maintenanceId?: string, options?: { enabled?: boolean }) =>
  useQuery<MaintenanceRow>({
    queryKey: [QUERY_KEY, 'MAINTENANCE', maintenanceId],
    queryFn: async () => await getNewMaintenanceRequester().getMaintenance(maintenanceId!),
    enabled: options?.enabled !== undefined ? options.enabled : maintenanceId !== '',
  });

const useGetMaintenanceReport = () =>
  useMutation<Blob, unknown, { request: MaintenanceReportRequest }>({
    mutationFn: (variables) => getNewMaintenanceRequester().getMaintenanceReport(variables.request),
  });

const useDeleteMaintenanceReminder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: DeleteMaintenanceReminderInput) => await getGraphQLMaintenanceRequester().deleteMaintenanceReminder(request),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY, 'MAINTENANCE_REMINDER'],
      }),
  });
};

const useValidateMaintenanceImport = (input: ValidateMaintenanceImportInput | null) => {
  return useQuery<ValidateMaintenanceImportResponse | undefined>({
    queryKey: [QUERY_KEY, 'MAINTENANCE_VALIDATE_IMPORT', input],
    queryFn: () => {
      if (!input) return undefined;
      return getGraphQLMaintenanceRequester().validateMaintenanceImport(input);
    },
    enabled: !!input,
  });
};

const useBatchImportMaintenanceHistory = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: ImportMaintenanceBatchInput[]) => getGraphQLMaintenanceRequester().importMaintenanceHistory(input),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY, 'MAINTENANCE_HISTORY'],
      });
    },
    onError: () => Alert.error('Erro ao importar dados. Tente novamente mais tarde.'),
  });
};

const useGetReminderTitles = () => {
  return useQuery({
    queryKey: [QUERY_KEY, 'MAINTENANCE_REMINDER', 'REMINDER_TITLES'],
    queryFn: () => getGraphQLMaintenanceRequester().getReminderTitles(),
  });
};

const useGetMaintenanceReminder = (input: GetReminderListInput) => {
  return useQuery<MaintenanceReminderResponse>({
    queryKey: [QUERY_KEY, 'MAINTENANCE_REMINDER', input],
    queryFn: () => getGraphQLMaintenanceRequester().getRemindersList(input),
  });
};

const useGetMaintenanceReminderStatusCount = (input: GetReminderStatusCountInput) => {
  return useQuery<ReminderStatusCount>({
    queryKey: [QUERY_KEY, 'MAINTENANCE_REMINDER', 'STATUS_SUMMARY', input],
    queryFn: () => getGraphQLMaintenanceRequester().getRemindersStatusCount(input),
  });
};

const useGetLatestMaintenanceByVehicle = (input: GetLatestMaintenanceInput, options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: [QUERY_KEY, 'LATEST_MAINTENANCE', input],
    queryFn: () => getGraphQLMaintenanceRequester().getLatestMaintenanceByVehicle(input),
    enabled: options?.enabled,
  });
};

export {
  useBatchImportMaintenanceHistory,
  useCreateMaintenanceHistory,
  useCreateMaintenanceParts,
  useCreateMaintenanceReminder,
  useDeleteMaintenanceHistory,
  useDeleteMaintenanceParts,
  useDeleteMaintenanceReminder,
  useGetMaintenance,
  useGetMaintenanceHistory,
  useGetMaintenanceParts,
  useGetMaintenanceReminder,
  useGetMaintenanceReminderStatusCount,
  useGetMaintenanceReport,
  useGetReminderTitles,
  useUpdateMaintenanceHistory,
  useUpdateMaintenanceParts,
  useUpdateMaintenanceReminder,
  useValidateMaintenanceImport,
  useGetLatestMaintenanceByVehicle,
};
